import { FOUNDRY } from '../data/maps/foundry.js';
import { DEFAULT_MAP_ID, type MapId, getMap } from '../data/maps/index.js';
import type { MatchConfig } from '../data/matchConfig.js';
import { MATCH_CONFIG_V0_4 } from '../data/matchConfig.js';
import type { SquadDef } from '../data/squads.js';
import { SQUAD_DEFS } from '../data/squads.js';
import type { FogGrid } from '../fog/FogGrid.js';
import { createFogGrid } from '../fog/FogGrid.js';
import type { RngState } from '../utils/rng.js';
import { createRng } from '../utils/rng.js';
import type { Vec2 } from '../utils/vec2.js';

import type { PlayerState } from './PlayerState.js';
import { createPlayerState } from './PlayerState.js';
import type { Building } from './entities/Building.js';
import type { ControlNode } from './entities/ControlNode.js';
import type { Core } from './entities/Core.js';
import type { NeutralCamp, NeutralUnit, SignalCache } from './entities/NeutralCamp.js';
import type { Projectile } from './entities/Projectile.js';
import type { ShieldBubble } from './entities/ShieldBubble.js';
import type { Squad, SquadArchetype, SquadMember } from './entities/Squad.js';
import type { EntityId, PlayerId, Tick } from './ids.js';
import { entityId, tickOf } from './ids.js';

export const TICK_MS = 50;
export const TICK_RATE_HZ = 1000 / TICK_MS;
export const SECONDS_PER_TICK = TICK_MS / 1000;
export const MATCH_LENGTH_SEC_DEFAULT = MATCH_CONFIG_V0_4.matchLengthSec;
export const SQUAD_CAP_V0_2 = MATCH_CONFIG_V0_4.squadCap;
export const SQUAD_CAP_V0_3 = MATCH_CONFIG_V0_4.squadCap;
export const SQUAD_CAP_V0_4 = MATCH_CONFIG_V0_4.squadCap;

export const WORLD_WIDTH = FOUNDRY.width;
export const WORLD_HEIGHT = FOUNDRY.height;

// Formation offsets (relative to squad pos) for each member index. Used for the
// visual placement of members; the sim hp pool is shared across members.
export const FORMATION_OFFSETS_PX: readonly Vec2[] = [
  { x: 0, y: 0 },
  { x: 12, y: 12 },
  { x: -12, y: 12 },
  { x: 0, y: -14 },
  { x: 14, y: -2 },
];

// MatchOutcome is set once by WinConditionSystem and never overwritten.
//   core_destroyed — a Core's hp crossed 0.
//   dominance      — regulation expired with a strict relay-count lead.
//   overtime       — regulation expired tied; OT resolved either by the 10 s
//                    continuous-lead rule ('overtime_dominance') or, if the
//                    60 s OT window expires without that condition, by Core HP
//                    tiebreak, then peak-income tiebreak. (V1.5 per
//                    docs/02_GAME_DESIGN.md §1 / §8.)
//
// V1.5 dropped 'core_hp_tiebreak' from the dominance variant — every regulation
// node tie now routes through overtime.
export type MatchOutcome =
  | { kind: 'core_destroyed'; loser: PlayerId; winner: PlayerId; tick: Tick }
  | {
      kind: 'dominance';
      winner: PlayerId;
      reason: 'nodes';
      tick: Tick;
    }
  | {
      kind: 'overtime';
      winner: PlayerId;
      reason: 'overtime_dominance' | 'core_hp_tiebreak' | 'peak_income_tiebreak';
      tick: Tick;
    };

// V1.5 match phase. 'regulation' is the default through 7:00 in V1.5+;
// 'signal_storm' covers the 7:00–8:00 final-pressure window (V1.5+ Slice 8 —
// global rule changes for forward-turret degradation + capture acceleration);
// 'overtime' if the dominance check ties at expiry of the storm. The
// transitions are: regulation → signal_storm at SIGNAL_STORM_START_SEC, then
// signal_storm → overtime at matchLengthSec when tied (or terminal outcome
// from the storm phase if a winner is decided at the timer). Systems that
// care about phase (HUD, win-check, stats, CombatSystem, CaptureSystem,
// TacticalBotPlayer) read it; never write it outside WinConditionSystem
// (Rule 1 — one writer per field).
export type MatchPhase = 'regulation' | 'signal_storm' | 'overtime';

// Per-match summary stats. Populated each tick by ProductionSystem (squadsBuilt
// increments) and StatsSystem (relayHistory samples). The end-of-match summary
// reads from here; the host never has to recompute. Plain data — round-trips
// through JSON like everything else (Rule 4).
//
// relayHistory is sampled every RELAY_HISTORY_SAMPLE_TICKS ticks (every 4 sim
// seconds at 50 ms tick) plus a final sample on match end, so an 8-minute match
// produces ~120 samples — small enough for a JSON snapshot, dense enough for a
// readable sparkline.
export const RELAY_HISTORY_SAMPLE_TICKS = 80;

export interface RelaySample {
  tick: Tick;
  matchSeconds: number;
  // Index 0 = player 0, index 1 = player 1. Number of relays held at the sample.
  nodesByPlayer: [number, number];
}

export interface MatchStats {
  squadsBuilt: [number, number];
  relayHistory: RelaySample[];
  // V1.5 telemetry — per-match counters used to validate the V1.5 acceptance
  // criteria with playtest data instead of anecdote (per
  // docs/03_MVP_SCOPE.md V1.5 acceptance: doctrine variety, surge comeback
  // rate, signal-link readability, anti-turtling). All round-trip through
  // JSON like the rest of MatchStats; the EOM screen surfaces a subset and
  // `apps/client/src/save/Telemetry.ts` persists the whole record per
  // completed match for paste-friendly aggregation across sessions.
  //
  // Update sites (Rule 12 — written where the rule lives):
  //   - doctrinePickedAtTick → Simulation.applyCommand on a successful
  //     PickDoctrineCommand. null when DoctrineSystem auto-assigns at
  //     window close (which happens AFTER the player explicitly didn't
  //     pick — useful signal: "did the player engage with the system?").
  //   - underdogSurgeSecondsByPlayer / Activations → EconomySystem each
  //     tick the flag is true / on rising-edge transitions.
  //   - isolatedRelaySecondsByPlayer → SignalLinkSystem per tick, summing
  //     SECONDS_PER_TICK for every owned-but-isolated relay belonging to
  //     each player.
  //   - campTier1ClearedAtTick / campTier2ClearedAtTick → CampSystem when
  //     a camp transitions to 'cleared'.
  //   - cachesPickedByPlayer → CampSystem when a SignalCache is collected.
  doctrinePickedAtTick: [Tick | null, Tick | null];
  underdogSurgeSecondsByPlayer: [number, number];
  underdogSurgeActivationsByPlayer: [number, number];
  isolatedRelaySecondsByPlayer: [number, number];
  campTier1ClearedAtTick: Tick | null;
  campTier2ClearedAtTick: Tick | null;
  cachesPickedByPlayer: [number, number];
  // V1.5 attribution telemetry — answers "who cleared each camp" and "how
  // long did it take", which the simpler ClearedAtTick + cachesPicked
  // counters can't disambiguate (the cache picker isn't always the player
  // who did the work). Damage counters accumulate while each tier is
  // active; the ClearedByPlayer field is set on the active→cleared
  // transition based on the leading damage. Null when neither player
  // dealt damage (e.g., tests that hand-zero unit hp) OR tied.
  campTier1DamageByPlayer: [number, number];
  campTier2DamageByPlayer: [number, number];
  campTier1ClearedByPlayer: PlayerId | null;
  campTier2ClearedByPlayer: PlayerId | null;
  campTier1ClearedDurationSec: number | null;
  campTier2ClearedDurationSec: number | null;
  // V1.5+ forward turret network dependency. Sum of seconds each player's
  // `mini_turret` buildings spent in `isOnline: false` state. High values for
  // one side mean their forward pressure was network-disrupted; low values
  // mean either no mini-turrets were deployed or the network held. Mirrors
  // `isolatedRelaySecondsByPlayer` shape; ForwardTurretNetworkSystem
  // accumulates per tick.
  forwardTurretOfflineSecondsByPlayer: [number, number];
  // V1.5+ Slice 2 — Cut Link telemetry. `cutLinksAppliedByPlayer` increments
  // on every successful channel completion (the AbilitySystem rising-edge
  // event when state.tick crosses `channelingAbility.expiresAtTick`).
  // `cutLinkActiveSecondsByPlayer` accumulates per-tick across every relay
  // currently sitting in a "cut" window owned by that player (mirrors
  // `forwardTurretOfflineSecondsByPlayer`'s shape so the telemetry summary
  // can render the two side-by-side as the disruption story).
  cutLinksAppliedByPlayer: [number, number];
  cutLinkActiveSecondsByPlayer: [number, number];
  // V1.5+ Slice 5 — Jam Relay telemetry. `jamRelaysAppliedByPlayer` increments
  // on every successful channel completion. `jamRelayActiveSecondsByPlayer`
  // accumulates per-tick across every relay currently sitting in a "jammed"
  // window owned by that player. Same shape as the cut-link counters so the
  // playtest summary can render the disruption story side-by-side.
  jamRelaysAppliedByPlayer: [number, number];
  jamRelayActiveSecondsByPlayer: [number, number];
  // V1.5+ Slice 6 — Disrupt Uplink telemetry. `disruptUplinksAppliedByPlayer`
  // increments on every successful channel completion.
  // `disruptUplinkActiveSecondsByPlayer` accumulates per-tick across every
  // relay currently sitting in a "disrupted" window owned by that player.
  // Same shape as the cut-link / jam-relay counters so the playtest summary
  // can render the disruption story side-by-side.
  disruptUplinksAppliedByPlayer: [number, number];
  disruptUplinkActiveSecondsByPlayer: [number, number];
  // V1.5+ Slice 7 — Fortify Relay telemetry. `fortifyRelaysAppliedByPlayer`
  // increments on every successful channel completion.
  // `fortifyRelayActiveSecondsByPlayer` accumulates per-tick across every
  // relay currently sitting in a "fortified" window owned by that player.
  // Same shape as the disruption counters so the playtest summary can
  // render the defensive story alongside the attacker stories. The §17
  // gate watches whether this counter rising correlates with a relay-flip
  // rate fall — that would be the failure mode (turtling-lite).
  fortifyRelaysAppliedByPlayer: [number, number];
  fortifyRelayActiveSecondsByPlayer: [number, number];
  // V1.5+ telemetry pre-flight: per-player relay flip count. A "flip" is a
  // single ownership transition where the new owner is non-null (so
  // neutralisation events don't double-count, only new captures).
  // CaptureSystem increments on the ownerId edge. Surfaced in
  // MatchTelemetryRow + TelemetrySummary so the canonical playtest harness
  // can answer Rule 17's "did relay-flip rate drop after the change?" gate.
  relayFlipsByPlayer: [number, number];
  // V1.5+ Slice 8 — Signal Storm Endgame telemetry. Per-player count of
  // relay flips that landed WHILE state.phase === 'signal_storm'. Subset
  // of relayFlipsByPlayer (every storm flip is also a regular flip). The
  // §17 final-minute requirement is closed by this metric: this slice's
  // pre/post comparison watches relayFlipsDuringStorm specifically (not
  // aggregate flip rate) — the storm is opt-in for whoever wants to push,
  // not a universal flip-rate amplifier. CaptureSystem increments on the
  // ownerId edge inside the storm window.
  signalStormFlipsByPlayer: [number, number];
}

// MatchState is plain data per Rule 4. No Maps, no Sets, no class instances, no functions.
// All entity tables key by EntityId (canonical from docs/06_DATA_MODEL.md). Buildings stay
// empty until V0.4; projectiles populate from V0.3 combat.
export interface MatchState {
  seed: number;
  tick: Tick;
  matchSeconds: number;
  matchLengthSec: number;
  startedAt: number;
  rng: RngState;
  nextEntityId: number;
  players: [PlayerState, PlayerState];
  cores: Record<EntityId, Core>;
  buildings: Record<EntityId, Building>;
  squads: Record<EntityId, Squad>;
  projectiles: Record<EntityId, Projectile>;
  controlNodes: Record<EntityId, ControlNode>;
  // V1 ability spawn: Phase Shield bubbles. Owned by a Shield squad's player; live
  // ShieldBubbles absorb damage to allied squads inside their radius. Reaped by
  // AbilitySystem when expiresAtTick passes or remaining absorb hits 0.
  shieldBubbles: Record<EntityId, ShieldBubble>;
  // Per-player fog grids. V1 introduces fog (docs/02_GAME_DESIGN.md §5); FogSystem
  // updates each tick. Plain data — round-trips through JSON cleanly.
  fog: [FogGrid, FogGrid];
  // V1 end-of-match summary support — see MatchStats above.
  stats: MatchStats;
  // V1.5 pacing & comeback fields. WinConditionSystem owns phase / overtimeStartedAtSec
  // / overtimeLeadPlayer / overtimeLeadHeldSec; EconomySystem owns underdogSurgeActive.
  // All round-trip through JSON cleanly (Rule 4).
  phase: MatchPhase;
  // V1.5+ Slice 8 — matchSeconds at which the storm phase began; null
  // while phase is still 'regulation'. Mirrors overtimeStartedAtSec's
  // shape so HUD readouts ("storm started 12 s ago") can be derived
  // without re-deriving from phase + matchSeconds. Set ONCE on the
  // regulation → signal_storm transition; never overwritten.
  signalStormStartedAtSec: number | null;
  // matchSeconds at which OT began; null while phase is regulation OR
  // signal_storm (overtime is the post-storm phase if the timer ties).
  overtimeStartedAtSec: number | null;
  // Player currently leading the relay count during OT. Null when tied or in regulation.
  overtimeLeadPlayer: PlayerId | null;
  // Continuous seconds the overtimeLeadPlayer has held a strict node lead. Reset on
  // tie or lead flip. WinConditionSystem ends the match when this hits
  // OVERTIME_LEAD_REQUIRED_SEC.
  overtimeLeadHeldSec: number;
  // Per-player Underdog Surge flag (HUD reads this). Index by PlayerId.
  underdogSurgeActive: [boolean, boolean];
  // V1.5 Doctrines (docs/02_GAME_DESIGN.md §6). DoctrineSystem flips
  // doctrinePickWindowOpen at the canonical sim time and closes it 15 s later.
  // doctrinePickWindowClosesAtTick is the absolute tick the window closes (HUD
  // reads it for a countdown). Both null/false in regulation before the window.
  doctrinePickWindowOpen: boolean;
  doctrinePickWindowClosesAtTick: Tick | null;
  // V1.5 neutral camps (docs/02_GAME_DESIGN.md §7, docs/03_MVP_SCOPE.md V1.5).
  // CampSystem owns these tables. The mid relay's `guardedBy` flag mirrors the
  // active camp's state. `neutralCampRespawnUsed` is a legacy snapshot field;
  // neutral camps no longer respawn after being cleared.
  neutralCamps: Record<EntityId, NeutralCamp>;
  neutralUnits: Record<EntityId, NeutralUnit>;
  signalCaches: Record<EntityId, SignalCache>;
  neutralCampRespawnUsed: boolean;
  // Match-config-derived flag. CampSystem early-exits when false so V0.x and
  // tests that don't want the camp can opt out. Lives on MatchState so the
  // simulation is self-contained — systems don't reach back into MatchConfig.
  enableNeutralCamps: boolean;
  // V1.5 signal links per docs/02_GAME_DESIGN.md §11 Option A. When true,
  // SignalLinkSystem computes `isConnected` for each owned relay. When false,
  // every owned relay reads connected (the V0.x default).
  enableSignalLinks: boolean;
  // V1.5 second map: the chosen MapDef id. Systems that need per-map static
  // data (CampSystem, Renderer, Minimap) look this up via MAPS[id]. Lives on
  // MatchState so the simulation is self-contained.
  mapId: MapId;
  outcome: MatchOutcome | null;
}

export function createInitialState(seed: number, config: MatchConfig = MATCH_CONFIG_V0_4): MatchState {
  const rng = createRng(seed);
  let nextId = 1;
  const allocate = (): EntityId => entityId(nextId++);
  const mapId: MapId = config.mapId ?? DEFAULT_MAP_ID;
  const map = getMap(mapId);

  const cores: Record<EntityId, Core> = {};
  for (const c of map.cores) {
    const id = allocate();
    cores[id] = {
      id,
      ownerId: c.ownerId,
      pos: { x: c.pos.x, y: c.pos.y },
      hp: 1500,
      maxHp: 1500,
      // V0.3: Cores produce squads directly. V0.4 swaps this to a Production Hub.
      produces: { queue: [], progress: 0 },
    };
  }

  const controlNodes: Record<EntityId, ControlNode> = {};
  for (const node of map.controlNodes) {
    const id = allocate();
    controlNodes[id] = {
      id,
      pos: { x: node.pos.x, y: node.pos.y },
      ownerId: null,
      captureProgress: 0,
      contested: false,
      generatesSignal: 0,
      guardedBy: null,
      // Default true so V0.x simulations (signal-link layer disabled) read
      // the flag as a no-op. SignalLinkSystem rewrites every tick when on.
      isConnected: true,
      // V1.5+ Slice 2 — Cut Link defaults. tickOf(0) means "not cut" (the
      // first sim tick is 0 → 1, so any state.tick >= 0 already passes the
      // `state.tick >= linkCutUntilTick` "expired" check). Set when
      // AbilitySystem completes a Cut Link channel.
      linkCutUntilTick: tickOf(0),
      linkCutByPlayerId: null,
      // V1.5+ Slice 5 — Jam Relay defaults. Same "tickOf(0) = not jammed"
      // convention as cut. Set when AbilitySystem completes a Jam Relay
      // channel.
      jammedUntilTick: tickOf(0),
      jammedByPlayerId: null,
      // V1.5+ Slice 6 — Disrupt Uplink defaults. Same "tickOf(0) = not
      // disrupted" convention. Set when AbilitySystem completes a Disrupt
      // Uplink channel.
      disruptedUntilTick: tickOf(0),
      disruptedByPlayerId: null,
      // V1.5+ Slice 7 — Fortify Relay defaults. Same "tickOf(0) = not
      // fortified" convention. Set when AbilitySystem completes a Fortify
      // Relay channel.
      fortifiedUntilTick: tickOf(0),
      fortifiedByPlayerId: null,
    };
  }

  const squads: Record<EntityId, Squad> = {};
  for (const spawn of map.spawns) {
    const id = allocate();
    const facing = spawn.ownerId === 0 ? Math.PI / 2 : -Math.PI / 2;
    squads[id] = makeSquad({
      id,
      ownerId: spawn.ownerId,
      archetype: 'recon',
      pos: { x: spawn.pos.x, y: spawn.pos.y },
      facing,
    });
  }

  const players: [PlayerState, PlayerState] = [
    createPlayerState(0, { name: 'Player 1', isBot: false, squadCap: config.squadCap }),
    createPlayerState(1, { name: 'Recruit Bot', isBot: true, squadCap: config.squadCap }),
  ];
  for (const sq of Object.values(squads)) {
    players[sq.ownerId].squadCount += 1;
  }

  const fogDims = { width: map.width, height: map.height, tilePx: map.tilePx };
  const fog: [FogGrid, FogGrid] = [createFogGrid(fogDims), createFogGrid(fogDims)];

  return {
    seed,
    tick: tickOf(0),
    matchSeconds: 0,
    matchLengthSec: config.matchLengthSec,
    startedAt: 0,
    rng,
    nextEntityId: nextId,
    players,
    cores,
    buildings: {},
    squads,
    projectiles: {},
    controlNodes,
    shieldBubbles: {},
    fog,
    // squadsBuilt counts pre-spawned squads so totals match what the player saw.
    // The first relay sample lands on the first StatsSystem run (tick 0 → 80).
    stats: {
      squadsBuilt: [
        countOwnedSpawns(map.spawns, 0),
        countOwnedSpawns(map.spawns, 1),
      ],
      relayHistory: [],
      doctrinePickedAtTick: [null, null],
      underdogSurgeSecondsByPlayer: [0, 0],
      underdogSurgeActivationsByPlayer: [0, 0],
      isolatedRelaySecondsByPlayer: [0, 0],
      campTier1ClearedAtTick: null,
      campTier2ClearedAtTick: null,
      cachesPickedByPlayer: [0, 0],
      campTier1DamageByPlayer: [0, 0],
      campTier2DamageByPlayer: [0, 0],
      campTier1ClearedByPlayer: null,
      campTier2ClearedByPlayer: null,
      campTier1ClearedDurationSec: null,
      campTier2ClearedDurationSec: null,
      forwardTurretOfflineSecondsByPlayer: [0, 0],
      cutLinksAppliedByPlayer: [0, 0],
      cutLinkActiveSecondsByPlayer: [0, 0],
      jamRelaysAppliedByPlayer: [0, 0],
      jamRelayActiveSecondsByPlayer: [0, 0],
      disruptUplinksAppliedByPlayer: [0, 0],
      disruptUplinkActiveSecondsByPlayer: [0, 0],
      fortifyRelaysAppliedByPlayer: [0, 0],
      fortifyRelayActiveSecondsByPlayer: [0, 0],
      relayFlipsByPlayer: [0, 0],
      signalStormFlipsByPlayer: [0, 0],
    },
    phase: 'regulation',
    signalStormStartedAtSec: null,
    overtimeStartedAtSec: null,
    overtimeLeadPlayer: null,
    overtimeLeadHeldSec: 0,
    underdogSurgeActive: [false, false],
    doctrinePickWindowOpen: false,
    doctrinePickWindowClosesAtTick: null,
    neutralCamps: {},
    neutralUnits: {},
    signalCaches: {},
    neutralCampRespawnUsed: false,
    enableNeutralCamps: config.enableNeutralCamps ?? false,
    enableSignalLinks: config.enableSignalLinks ?? false,
    mapId,
    outcome: null,
  };
}

function countOwnedSpawns(spawns: ReadonlyArray<{ ownerId: PlayerId }>, ownerId: PlayerId): number {
  let n = 0;
  for (const s of spawns) {
    if (s.ownerId === ownerId) n += 1;
  }
  return n;
}

export function makeSquad(args: {
  id: EntityId;
  ownerId: PlayerId;
  archetype: SquadArchetype;
  pos: Vec2;
  facing: number;
}): Squad {
  const def: SquadDef = SQUAD_DEFS[args.archetype];
  const members: SquadMember[] = [];
  for (let i = 0; i < def.members; i++) {
    const offset = FORMATION_OFFSETS_PX[i] ?? FORMATION_OFFSETS_PX[0];
    if (offset === undefined) throw new Error('formation offsets table empty');
    members.push({ alive: true, offset: { x: offset.x, y: offset.y } });
  }
  return {
    id: args.id,
    ownerId: args.ownerId,
    archetype: args.archetype,
    pos: { x: args.pos.x, y: args.pos.y },
    facing: args.facing,
    hp: def.maxHp,
    maxHp: def.maxHp,
    vision: def.vision,
    speed: def.speed,
    abilityCooldown: 0,
    secondaryAbilityCooldown: 0,
    attackCooldown: 0,
    orderQueue: [],
    path: [],
    pathIndex: 0,
    members,
    target: null,
    markedUntilTick: tickOf(0),
    markedByPlayerId: null,
    suppressedUntilTick: tickOf(0),
    channelingAbility: null,
    lastAttackerPlayerId: null,
  };
}

export function snapshot(state: MatchState): MatchState {
  return JSON.parse(JSON.stringify(state)) as MatchState;
}

export function getPlayer(state: MatchState, id: PlayerId): PlayerState {
  return state.players[id];
}
